/**
 * `DataTableSummaryBand` — the preset's pinned aggregate band: one engine row rendered
 * through the SAME visible leaf columns as the body, wrapped in the sticky `TableRow` band.
 * Split out of `data-table.tsx` for the same reason as `DataTableHeaderRow` — the orchestrator
 * holds the band as a single node instead of a second cell-derivation block.
 *
 * REACTIVITY: the cell tracks (width / grow flex / pinned offset / alignment) are derived HERE,
 * in this component's own render — through the same `getCellTrackProps` the body rows use — off
 * the referentially-stable `table`, reads the React Compiler cannot see change. `pinnedOffsets`
 * is the carrier that keeps them live: the preset MEMOIZES that map on exactly the inputs the
 * pinned geometry derives from (`PinnedGeometryMemoKey` — the four state slices plus the
 * column defs), so a new identity arrives precisely when a resize / visibility / order / pin
 * change lands, and NOT on unrelated renders. Same mechanism `DataTableHeaderRow` relies on;
 * anything added to that geometry must join the key, or these tracks freeze.
 */
import type { CSSProperties } from 'react'
import { flexRender, type Row, type RowData } from '@tanstack/react-table'

import { cn } from '@gto/ui/cn'

import type { DataTableSummaryRowProps } from '../data-table.types'
import { readSummaryCell } from './column-meta'
import { getCellTrackProps } from './column-size'
import {
  type DataTableDensity,
  type DataTableDivider,
  dataTableSummaryBandVariants,
  type DataTableSurface,
} from './data-table.styles'
import { pinnedCellSurfaceClass, type PinnedOffsetMap } from './pinning.styles'
import { SELECTION_COLUMN_WIDTH_PX, TableCell, TableRow } from './table-parts'

/** A band's sticky edge and its distance from that edge — see `DataTableSummaryBandProps.offset`. */
export type DataTableSummaryBandOffset = { top: number } | { bottom: number }

export type DataTableSummaryBandProps<TData extends RowData> = {
  /**
   * The aggregate row, built with TanStack's `createRow` against the live table — so its
   * `getVisibleCells()` honours the current visibility / order / pinning, and each cell's
   * context feeds the column def's own `cell` renderer. It is NOT part of the engine's row
   * model, which is what keeps every count blind to it.
   */
  row: Row<TData>
  density: DataTableDensity
  /** The band's elevation — opaque, so body rows scrolling under it stay hidden. */
  surface: DataTableSurface
  /**
   * The table's row-separator model. It picks the COLOUR of the band's body-facing edge, so
   * the band matches the rows it caps. The band's own row model never follows it.
   */
  divider: DataTableDivider
  pinnedOffsets: PinnedOffsetMap
  /** Whether the body uses grow columns, which binds every plane to the scrollport. */
  fitScrollport: boolean
  /**
   * A `selection` model renders a model-external leading column, so the band opens with an
   * EMPTY cell of the same width (no checkbox — an aggregate is not selectable) and every
   * data cell's `aria-colindex` shifts by one, keeping the column tracks aligned.
   */
  hasSelectionColumn: boolean
  /** Sticky style for the leading cell when a data column is left-pinned; from `useSelectionColumn`. */
  selectionPinnedStyle?: CSSProperties
  /** Consumer props merged onto the band (test hooks, `aria-label`, …). */
  rowProps?: DataTableSummaryRowProps
  /**
   * Where the band pins, and how far from that edge, in px. `{ bottom }` pins it to the
   * scrollport's bottom edge, lifted by the bands stacked under it (`0` for the lowest).
   * `{ top }` pins it under the sticky header: the header's height plus the bands stacked above
   * it. The key picks the edge. Inline rather than a variant because it is geometry the stack
   * derives per band. It is ALWAYS written, `0` included: the band carries no edge class, because
   * a host sheet can set that class with `!important` (see `dataTableSummaryBandVariants`).
   *
   * One shared sticky wrapper around the bands would remove the per-band offset and the inline
   * style with it, and review argued it should. It is NOT ruled out — it was left alone because
   * it nests the `role="row"` bands one level deeper inside `role="grid"`, and nobody has yet
   * checked a screen reader or a real browser against that. Take the wrapper when someone can
   * verify it; the ratchet carries a `libs/ui.inlineStyles` exception until they do.
   */
  offset?: DataTableSummaryBandOffset
}

/**
 * Plain generic function (not `FC`) so `Row<TData>` inference survives — same reason as the
 * header row / menu components.
 */
export const DataTableSummaryBand = <TData extends RowData>({
  row,
  density,
  surface,
  divider,
  pinnedOffsets,
  fitScrollport,
  hasSelectionColumn,
  selectionPinnedStyle,
  rowProps,
  offset = { bottom: 0 },
}: DataTableSummaryBandProps<TData>) => {
  const { className: consumerClassName, style: consumerStyle, ...restRowProps } = rowProps ?? {}
  const placement = 'top' in offset ? 'top' : 'bottom'
  const offsetPx = 'top' in offset ? offset.top : offset.bottom
  const dataColOffset = hasSelectionColumn ? 1 : 0
  // Hoisted rather than inlined: `@stylistic/jsx-sort-props` wants a multiline prop last, and
  // `className` belongs beside the other row props here, not after the spread.
  const bandClassName = cn(
    dataTableSummaryBandVariants({ surface, placement, divider }),
    consumerClassName,
  )

  return (
    <TableRow
      data-summary-row
      // The bordered row model (square, contiguous) for EVERY table, whatever the body
      // draws: a floating band would round its corners mid-scroll, and an `'inset'` one would
      // trade its body-facing edge for a stray hairline at the row top. `bandClassName` sets
      // the edge and its colour over this.
      divider
      className={bandClassName}
      density={density}
      fitScrollport={fitScrollport}
      // A chrome band, not a data row — never hover-tints (DataGridPro footer parity).
      hoverTint={false}
      // `TableRow` emits `role="row"` only alongside an `aria-rowindex`; the band carries the
      // role WITHOUT the index on purpose — see `DataTableSummaryRow` in `data-table.types.ts`.
      role="row"
      // The stack offset first, so a consumer's own `style` still composes over it.
      style={{ [placement]: offsetPx, ...consumerStyle }}
      {...restRowProps}
    >
      {hasSelectionColumn && (
        <TableCell
          align="center"
          // Opaque in the band's OWN surface, like the pinned data cells beside it.
          className={pinnedCellSurfaceClass(surface)}
          colIndex={1}
          style={{ width: SELECTION_COLUMN_WIDTH_PX, ...selectionPinnedStyle }}
        />
      )}
      {row.getVisibleCells().map((cell, colIndex) => (
        <TableCell
          key={cell.id}
          colIndex={colIndex + 1 + dataColOffset}
          {...getCellTrackProps(cell, pinnedOffsets, surface)}
        >
          {/*
            `meta.summaryCell` when the column formats its total differently, else the
            column's own `cell` — the same context either way, so a column needs no
            "am I the summary row?" branch inside one renderer.
          */}
          {flexRender(readSummaryCell(cell) ?? cell.column.columnDef.cell, cell.getContext())}
        </TableCell>
      ))}
    </TableRow>
  )
}
